import Typography from '@material-ui/core/Typography';
import React, { useEffect } from 'react';

import { useTranslation } from '../../../hooks/useTranslation';
import { RegistrationFormProps } from '../registration.types';
import { Box, Divider, Grid } from '@material-ui/core';
import Dropdown from '../../dropdown';
import TextField from '../../text-field';
import DatePicker from '../../date-picker';
import moment from 'moment-timezone';
import SSNTextField from '../../ssn-text-field';
import { PersonPrimaryIdType } from '../../../__generated__/pge-types';
import { statesAndProvincesGrouping } from '../../utility/state-dropdown';

interface NewIDVerificationProps extends RegistrationFormProps {
  classes: any;
}

const CALENDAR_INITIAL_DATE_SELECTION = '01/01/1990';
const DATE_FORMAT = 'MM/dd/yyyy';

const NewUserIdentity = (props: NewIDVerificationProps) => {
  const { t } = useTranslation();
  const { classes } = props;
  const { form } = props;
  const initialDateSelection = moment(CALENDAR_INITIAL_DATE_SELECTION).toDate();

  useEffect(() => {
    //Select SSN as a default ID type if the value is not selected by the user
    if (form.values.IDType === PersonPrimaryIdType.Ein) {
      void form.setValue('IDType', PersonPrimaryIdType.Ssn);
      void form.setValue('IDValue', '');
    }
  }, []);

  return (
    <div style={{ padding: 0 }}>
      <Grid
        container
        spacing={1}
        style={{
          justifyContent: 'flex-start',
        }}
      >
        <Grid item xs={12}>
          <Typography variant={'h2'} style={{ padding: '0 0 8px 0' }}>
            {t('SIGNUP_PROVIDE_YOUR_INFO')}
          </Typography>
          <Typography>{t('SIGNUP_PROVIDE_YOUR_INFO_DESCRIPTION')}</Typography>
        </Grid>

        <Grid item xs={12} md={12}>
          <TextField
            name={'firstName'}
            required
            data-testid="firstName"
            label={t('FIRST_NAME')}
            inputProps={{
              minLength: 1,
              maxLength: 60,
            }}
            style={{ width: '100%' }}
            {...form.props('firstName')}
            error={Boolean(form.errors.firstName)}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            name={'middleName'}
            data-testid="middleName"
            label={t('MIDDLE_NAME')}
            inputProps={{ minLength: 1, maxLength: 60 }}
            style={{ width: '100%' }}
            {...form.props('middleName')}
            error={Boolean(form.errors.middleName)}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            name={'lastName'}
            required
            data-testid="lastName"
            label={t('LAST_NAME')}
            inputProps={{ minLength: 1, maxLength: 60 }}
            style={{ width: '100%' }}
            {...form.props('lastName')}
            error={Boolean(form.errors.lastName)}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <DatePicker
            style={{ width: '100%' }}
            required
            label={t('DATE_OF_BIRTH')}
            data-testid="birth-date-picker"
            name="date"
            format={DATE_FORMAT}
            initialFocusedDate={initialDateSelection}
            {...form.props('birthdate')}
            mask={'__/__/____'}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <Divider css={{ margin: '16px 0' }} />
        </Grid>

        <Grid item xs={12}>
          <Typography variant={'h2'} style={{ padding: '0 0 8px 0' }}>
            {t('SIGNUP_PROVIDE_YOUR_ID_INFO')}
          </Typography>
          <Typography style={{ marginBottom: 4, marginTop: 4 }}>
            {t('SIGNUP_PROVIDE_YOUR_ID_INFO_DESC')}
          </Typography>
        </Grid>

        <Grid container>
          <Grid
            item
            xs={12}
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '16px',
            }}
          >
            <Box style={{ margin: '8px 0' }}>
              <Dropdown
                label={''}
                value={form.values.IDType}
                selectionList={[
                  {
                    value: PersonPrimaryIdType.Ssn,
                    label: t('SOCIAL_SECURITY_NUMBER'),
                  },
                  {
                    value: PersonPrimaryIdType.Dl,
                    label: t('DRIVERS_LICENSE'),
                  },
                ]}
                onChange={e => {
                  void form.setValue('IDType', e.target.value);
                  void form.setValue('IDValue', '');
                }}
              />
            </Box>
            {form.values.IDType === PersonPrimaryIdType.Ssn && (
              <SSNTextField
                name="IDValue"
                data-testid="ssn-field"
                label={t('SOCIAL_SECURITY_NUMBER')}
                style={{
                  width: '100%',
                  background: 'transparent',
                }}
                className={classes.root}
                {...form.props('IDValue')}
                error={Boolean(form.errors.IDValue)}
              />
            )}

            {form.values.IDType === PersonPrimaryIdType.Dl && (
              <>
                <Box style={{ margin: '8px 0' }}>
                  <Dropdown
                    data-testid="state-field"
                    label={''}
                    value={form.values.IDState}
                    selectionList={[
                      { label: 'OR', value: 'OR' },
                      ...statesAndProvincesGrouping.unitedStates
                        .filter((v: string) => v !== 'OR')
                        .map((v: string) => ({ label: v, value: v })),
                    ]}
                    onChange={e => {
                      void form.setValue('IDState', e.target.value);
                    }}
                  />
                </Box>
                <TextField
                  data-testid="dl-field"
                  inputProps={{
                    maxLength: 19,
                    style: { background: '#FFF' },
                  }}
                  style={{
                    width: '100%',
                    background: 'transparent',
                  }}
                  name="IDValue"
                  {...form.props('IDValue')}
                  error={Boolean(form.errors.IDValue)}
                  label={t('DRIVERS_LICENSE')}
                  delayedTextMask={true}
                />
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default NewUserIdentity;
